import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ScanLine, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Breadcrumb from "src/components/ui/Breadcrumb";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";
import { InfoLabel } from "src/components/ui/InfoIcon";
import ScanProductModal from "src/components/modals/ScanProductModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as stockActions from "src/redux/slices/stockSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import productApis from "src/api/product";
import stockApis from "src/api/stock";
import { Product } from "src/types/product.types";
import { formatVariantLabel } from "src/utils/common";

interface TransferRow {
  key: number;
  product: Product | null;
  productLabel: string;
  variantId: string;
  quantity: string;
  available: number | null;
}

let rowKeySeq = 0;
const emptyRow = (): TransferRow => ({
  key: rowKeySeq++,
  product: null,
  productLabel: "",
  variantId: "",
  quantity: "",
  available: null,
});

const NewStockTransferPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const { createStockTransfer } = useAppSelector((state) => state.stock);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreate =
    isTenantAdmin || (permissions?.transfers || []).includes("create");
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel } = useWarehouseLabel();

  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");

  // Locked to the "from" side only — a warehouse-scoped staff member moves
  // stock OUT of their own warehouse, but can still send it to any other.
  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setFromWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);
  const [rows, setRows] = useState<TransferRow[]>([emptyRow()]);
  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [highlightedRowKey, setHighlightedRowKey] = useState<number | null>(null);

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (createStockTransfer.data && submitted) {
      toast.success("Stock transfer created successfully");
      dispatch(stockActions.resetCreateStockTransfer());
      setSubmitted(false);
      navigate("/app/stock/transfers");
    } else if (createStockTransfer.error && submitted) {
      toast.error(`Error: ${createStockTransfer.error}`);
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createStockTransfer, submitted]);

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  const updateRow = (key: number, patch: Partial<TransferRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (key: number) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  // Available = quantity - reserved at the source (from) warehouse.
  const fetchAvailability = async (
    key: number,
    productId: string,
    variantId?: string
  ) => {
    if (!fromWarehouseId || !productId) return;
    try {
      const res = await stockApis.getStockLevels({
        warehouseId: fromWarehouseId,
        productId,
        ...(variantId ? { variantId } : {}),
      });
      const level = (res?.data?.data || res?.data || [])[0];
      const available = level ? Number(level.available ?? 0) : 0;
      setRows((prev) =>
        prev.map((r) => (r.key === key ? { ...r, available } : r))
      );
    } catch (error) {
      console.error("Failed to fetch availability", error);
    }
  };

  // Re-check availability for every row already carrying a product whenever
  // the source warehouse changes.
  useEffect(() => {
    if (!fromWarehouseId) return;
    rows.forEach((row) => {
      if (row.product?._id) fetchAvailability(row.key, row.product._id, row.variantId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromWarehouseId]);

  useEffect(() => {
    if (highlightedRowKey === null) return;
    const timer = setTimeout(() => setHighlightedRowKey(null), 1500);
    return () => clearTimeout(timer);
  }, [highlightedRowKey]);

  const applyScannedProduct = (product: Product, variantId: string) => {
    const normalizedVariantId = variantId || "";
    const existingIdx = rows.findIndex(
      (r) => r.product?._id === product._id && (r.variantId || "") === normalizedVariantId
    );

    if (existingIdx !== -1) {
      const existing = rows[existingIdx];
      updateRow(existing.key, { quantity: String((Number(existing.quantity) || 0) + 1) });
      toast.success(`Increased quantity for ${product.name}`);
      setHighlightedRowKey(existing.key);
      return;
    }

    const patch: Partial<TransferRow> = {
      product,
      productLabel: `${product.name} (${product.sku || product.productId})`,
      variantId: normalizedVariantId,
      quantity: "1",
      available: null,
    };

    const lastRow = rows[rows.length - 1];
    let targetKey: number;
    if (lastRow && !lastRow.product) {
      targetKey = lastRow.key;
      updateRow(targetKey, patch);
    } else {
      const newRow: TransferRow = { ...emptyRow(), ...patch };
      targetKey = newRow.key;
      setRows((prev) => [...prev, newRow]);
    }

    toast.success(`Added ${product.name}`);
    setHighlightedRowKey(targetKey);
    if (product._id) fetchAvailability(targetKey, product._id, normalizedVariantId || undefined);
  };

  const rowsValid = rows.every(
    (r) =>
      r.product?._id &&
      (r.product.productType !== "variant" || r.variantId) &&
      Number(r.quantity) > 0 &&
      (r.available === null || Number(r.quantity) <= r.available)
  );
  const isValid =
    !!fromWarehouseId &&
    !!toWarehouseId &&
    fromWarehouseId !== toWarehouseId &&
    rows.length > 0 &&
    rowsValid;

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid || !canCreate) return;
    setSubmitted(true);
    dispatch(
      stockActions.createStockTransferRequest({
        fromWarehouseId,
        toWarehouseId,
        items: rows.map((r) => ({
          productId: r.product!._id!,
          ...(r.product!.productType === "variant" ? { variantId: r.variantId } : {}),
          quantity: Number(r.quantity),
        })),
      })
    );
  };

  return (
    <DashboardLayout
      meta={{
        title: "New Stock Transfer | Invyntra",
        description: `Move stock from one ${warehouseLabel.toLowerCase()} to another.`,
      }}
    >
      <div className="space-y-4">
        <Breadcrumb
          data={[
            { label: "Stock transfers", path: "/app/stock/transfers" },
            { label: "New transfer" },
          ]}
        />

        <Card className="space-y-5">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              New stock transfer
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Move stock from one {warehouseLabel.toLowerCase()} to another. For products with
              multiple variants, pick the variant to move.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FilterSelect
              variant="boxed"
              label={`From ${warehouseLabel.toLowerCase()}`}
              required
              value={fromWarehouseId}
              onChange={setFromWarehouseId}
              allLabel="Select"
              options={warehouseOptions}
              error={touched && !fromWarehouseId}
              disabled={isWarehouseLocked}
              helperText={
                isWarehouseLocked
                  ? `Locked to your assigned ${warehouseLabel.toLowerCase()}.`
                  : undefined
              }
            />
            <FilterSelect
              variant="boxed"
              label={`To ${warehouseLabel.toLowerCase()}`}
              required
              value={toWarehouseId}
              onChange={setToWarehouseId}
              allLabel="Select"
              options={warehouseOptions}
              error={
                touched && (!toWarehouseId || toWarehouseId === fromWarehouseId)
              }
              helperText={
                touched && toWarehouseId && toWarehouseId === fromWarehouseId
                  ? `Must be different from the source ${warehouseLabel.toLowerCase()}`
                  : undefined
              }
            />
          </div>

          <div className="space-y-3 border-t border-gray-100 pt-4 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Products
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Scan barcode"
                  disabled={!fromWarehouseId}
                  onClick={() => setScanOpen(true)}
                >
                  <ScanLine size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={addRow}>
                  <Plus size={14} className="mr-1" />
                  Add Product
                </Button>
              </div>
            </div>

            {!fromWarehouseId && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Pick a from {warehouseLabel.toLowerCase()} above to search products and see what's available.
              </p>
            )}

            <div className="space-y-3">
              {rows.map((row) => {
                const isVariantProduct = row.product?.productType === "variant";
                const variantOptions = (row.product?.variants || []).map((v) => ({
                  label: formatVariantLabel(v.optionValues) || v.sku || "Variant",
                  value: v._id || "",
                  disabled: rows.some(
                    (r) =>
                      r.key !== row.key &&
                      r.product?._id === row.product?._id &&
                      r.variantId === v._id
                  ),
                }));
                const isHighlighted = row.key === highlightedRowKey;
                return (
                  <div
                    key={row.key}
                    className={`rounded-xl border p-3 transition-colors ${
                      isHighlighted
                        ? "border-primary-300 bg-primary-50/60 ring-2 ring-primary-300 dark:border-primary-700 dark:bg-primary-900/20 dark:ring-primary-700"
                        : "border-gray-100 dark:border-gray-700"
                    }`}
                  >
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="min-w-[260px] flex-1">
                        <SearchInput<Product>
                          label="Product"
                          placeholder="Select product"
                          value={row.productLabel}
                          onSearch={async (query) => {
                            const res = await productApis.getProductList({
                              page: 1,
                              limit: 10,
                              status: "active",
                              search: query,
                            });
                            const list: Product[] = res?.data?.data || res?.data || [];
                            return list.map((p) => ({
                              value: p._id || "",
                              label: `${p.name} (${p.sku || p.productId})`,
                              data: p,
                            }));
                          }}
                          disabled={!fromWarehouseId}
                          onSelect={(option: SearchInputOption<Product>) => {
                            updateRow(row.key, {
                              product: option.data,
                              productLabel: option.label,
                              variantId: "",
                              available: null,
                            });
                            if (option.data.productType !== "variant" && option.data._id) {
                              fetchAvailability(row.key, option.data._id, undefined);
                            }
                          }}
                          onClear={() =>
                            updateRow(row.key, {
                              product: null,
                              productLabel: "",
                              variantId: "",
                              available: null,
                            })
                          }
                          renderOption={(option) => (
                            <div>
                              <div className="font-medium text-gray-800 dark:text-gray-100">
                                {option.data.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                SKU: {option.data.sku || "-"} · HSN:{" "}
                                {option.data.hsnCode || "-"}
                              </div>
                            </div>
                          )}
                        />
                      </div>

                      {isVariantProduct && (
                        <div className="w-56">
                          <FilterSelect
                            variant="boxed"
                            label="Variant"
                            required
                            value={row.variantId}
                            onChange={(val) => {
                              updateRow(row.key, { variantId: val, available: null });
                              if (row.product?._id) fetchAvailability(row.key, row.product._id, val);
                            }}
                            allLabel="Select"
                            options={variantOptions}
                            error={touched && !row.variantId}
                          />
                        </div>
                      )}

                      {row.product && (
                        <div className="w-24">
                          <InfoLabel
                            label="Available"
                            info={`On-hand quantity at the from ${warehouseLabel.toLowerCase()}, minus whatever's already reserved by other pending sale orders there. This is the most you can move out right now.`}
                          />
                          <div
                            className={`mt-1 flex h-9 w-full items-center justify-center rounded-md border text-sm font-medium ${
                              row.available !== null && Number(row.quantity) > row.available
                                ? "border-red-300 bg-red-50 text-red-600 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-400"
                                : "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300"
                            }`}
                          >
                            {row.available === null ? "…" : row.available}
                          </div>
                        </div>
                      )}

                      <div className="w-28">
                        <InfoLabel
                          label="Qty"
                          info={`How many units to move to the destination ${warehouseLabel.toLowerCase()}. Can't exceed what's Available at the source — once created, the transfer immediately decreases stock here and increases it at the destination.`}
                        />
                        <div className="mt-1">
                          <Input
                            type="number"
                            min={1}
                            value={row.quantity}
                            onChange={(e) => updateRow(row.key, { quantity: e.target.value })}
                            error={
                              touched &&
                              (!(Number(row.quantity) > 0) ||
                                (row.available !== null && Number(row.quantity) > row.available))
                            }
                          />
                        </div>
                      </div>

                      <button
                        type="button"
                        className="mt-6 flex items-center gap-1 text-sm text-red-600 hover:text-red-700 disabled:opacity-40 dark:text-red-400"
                        onClick={() => removeRow(row.key)}
                        disabled={rows.length === 1}
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => navigate("/app/stock/transfers")}
              disabled={createStockTransfer.loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={createStockTransfer.loading}
              disabled={createStockTransfer.loading || !canCreate}
              title={
                !canCreate
                  ? "You don't have permission to create stock transfers"
                  : undefined
              }
            >
              Create transfer
            </Button>
          </div>
        </Card>
      </div>

      <ScanProductModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onProductFound={applyScannedProduct}
      />
    </DashboardLayout>
  );
};

export default NewStockTransferPage;
